export function serviceWorkerUrl(buildSha) {
  const version = typeof buildSha === 'string' && buildSha ? buildSha : 'dev';
  return `/sw.js?v=${encodeURIComponent(version)}`;
}

const VERSION_CHECK_TIMEOUT_MS = 4_000;

export function registerServiceWorker(serviceWorker, buildSha) {
  if (typeof serviceWorker?.register !== 'function') return null;
  // The URL is deployment-keyed, and bypassing the browser's HTTP cache keeps
  // Safari from reusing an older worker response while thawing a suspended tab.
  return serviceWorker.register(serviceWorkerUrl(buildSha), { updateViaCache: 'none' });
}

/**
 * A root document is reloadable only while the bare homepage owns the visible
 * interaction. Homepage replay opens intentionally keep `/` in the address
 * bar, so pathname + app screen state alone would let a mobile resume/version
 * check reload the document underneath active playback.
 */
export function isSafeHomepageReload(app, {
  locationTarget = globalThis.location,
  documentTarget = globalThis.document,
} = {}) {
  return locationTarget?.pathname === '/'
    && app?.state === 'menu'
    && (app?._activeScreenName == null || app._activeScreenName === 'menu')
    && !app?._activeReplayViewerClose
    && !documentTarget?.getElementById?.('replay-viewer');
}

function controllerBuildSha(serviceWorker) {
  const scriptUrl = serviceWorker?.controller?.scriptURL;
  if (!scriptUrl) return null;
  try {
    return new URL(scriptUrl, globalThis.location?.href).searchParams.get('v');
  } catch {
    return null;
  }
}

/**
 * Reload a safe-to-reload homepage document when a mobile tab resumes on an
 * older deployment. Existing documents keep their already-evaluated JS when a
 * new worker takes control, so worker replacement by itself cannot turn an
 * open v1 document into the v2 app.
 *
 * Gameplay is deliberately excluded by the caller's shouldReload predicate.
 */
export function monitorServiceWorkerFreshness(serviceWorker, {
  buildSha,
  buildVersion,
  shouldReload = () => true,
  fetchImpl = globalThis.fetch?.bind(globalThis),
  reload = () => globalThis.location?.reload(),
  documentTarget = globalThis.document,
  windowTarget = globalThis.window,
  versionCheckTimeoutMs = VERSION_CHECK_TIMEOUT_MS,
} = {}) {
  if (!serviceWorker || !documentTarget || !windowTarget) return null;

  let reloadRequested = false;
  let checkInFlight = null;

  const requestReload = () => {
    if (reloadRequested || !shouldReload()) return false;
    reloadRequested = true;
    reload();
    return true;
  };

  const checkServerVersion = () => {
    if (reloadRequested || documentTarget.visibilityState !== 'visible' || !fetchImpl) {
      return Promise.resolve(false);
    }
    if (checkInFlight) return checkInFlight;
    const abortController = typeof globalThis.AbortController === 'function'
      ? new globalThis.AbortController()
      : null;
    let timeoutId;
    let expired = false;
    const timeout = new Promise((resolve) => {
      timeoutId = setTimeout(() => {
        expired = true;
        abortController?.abort();
        resolve(false);
      }, versionCheckTimeoutMs);
    });
    const request = Promise.resolve().then(() => fetchImpl('/api/version', {
      cache: 'no-store',
      credentials: 'same-origin',
      ...(abortController ? { signal: abortController.signal } : {}),
    })).then(async (response) => {
      if (expired || !response.ok) return false;
      const current = await response.json();
      // Some fetch implementations do not honor AbortSignal. Ignore a late
      // response after the timeout so an expired probe cannot reload the page.
      if (expired) return false;
      if (current?.version && buildVersion && current.version !== buildVersion) {
        return requestReload();
      }
      return false;
    });
    checkInFlight = Promise.race([request, timeout])
      .catch(() => false)
      .finally(() => {
        clearTimeout(timeoutId);
        checkInFlight = null;
      });
    return checkInFlight;
  };

  const onControllerChange = () => {
    const controlledSha = controllerBuildSha(serviceWorker);
    if (controlledSha && buildSha && controlledSha !== buildSha) requestReload();
  };
  const onVisibilityChange = () => {
    if (documentTarget.visibilityState === 'visible') void checkServerVersion();
  };
  const onPageShow = (event) => {
    if (event?.persisted) void checkServerVersion();
  };

  serviceWorker.addEventListener?.('controllerchange', onControllerChange);
  documentTarget.addEventListener?.('visibilitychange', onVisibilityChange);
  windowTarget.addEventListener?.('pageshow', onPageShow);

  return {
    check: checkServerVersion,
    dispose() {
      serviceWorker.removeEventListener?.('controllerchange', onControllerChange);
      documentTarget.removeEventListener?.('visibilitychange', onVisibilityChange);
      windowTarget.removeEventListener?.('pageshow', onPageShow);
    },
  };
}
